// Weekly hours constraint: For each week and for each user, the sum of
// assigned event durations must be <= the user's weeklyHoursMax.
// Requires:
// - model.participants: [{ uid, weeklyHoursMax, selected }]
// - model.optimizationIndexes: { users, eventInSameWeek, eventDuration }
export function buildWeeklyHoursConstraints(model, _params = {}) {
  const idx = model?.optimizationIndexes || {};
  const usersMap = idx.users || {}; // U# -> userId
  const weeks = idx.eventInSameWeek || {}; // weekKey -> [E#]
  // Prefer integer minutes to avoid fractional coefficients in CP-SAT
  const eventMinutes = idx.eventEffectiveMinutes || idx.eventDurationMinutes || {}; // E# -> minutes
  const staffRequiredByRole = idx.staffRequiredByRole || {}; // E# -> { R#: count }
  const participants = Array.isArray(model?.participants) ? model.participants : [];
  const declared = new Set(Object.keys(idx.decisionVariables || {}));
  // Build lookup: userKey -> weeklyHoursMax with fallbacks (settings -> personalized -> modal override)
  const weeklyDefaults = (model && model.weeklyDefaults) || {};
  const defaultInternal = Number(weeklyDefaults.internal) || 40;
  const defaultExternal = Number(weeklyDefaults.external) || 44;
  const employmentByUser = idx.employmentByUser || {}; // U# -> 'internal' | 'external'
  const weeklyMaxByUid = participants.reduce((acc, p) => {
    acc[p.uid] = Number(p.weeklyHoursMax);
    return acc;
  }, {});
  const weeklyMaxByUserKey = Object.entries(usersMap).reduce((acc, [userKey, uid]) => {
    const specified = Number(weeklyMaxByUid[uid]);
    if (Number.isFinite(specified) && specified > 0) {
      acc[userKey] = specified;
    } else {
      const contract = employmentByUser[userKey] === 'external' ? 'external' : 'internal';
      acc[userKey] = contract === 'external' ? defaultExternal : defaultInternal;
    }
    return acc;
  }, {});

  const constraints = [];
  for (const [, eventKeys] of Object.entries(weeks)) {
    const eKeys = Array.isArray(eventKeys) ? eventKeys : [];
    for (const userKey of Object.keys(usersMap)) {
      const vars = [];
      const coeffs = [];
      for (const eKey of eKeys) {
        const roles = Object.keys(staffRequiredByRole[eKey] || {});
        for (const rKey of roles) {
          const name = `x_${userKey}_${eKey}_${rKey}`;
          if (declared.has(name)) {
            vars.push(name);
            // integer minutes
            coeffs.push(Math.max(0, Math.round(Number(eventMinutes[eKey] || 0))));
          }
        }
      }
      if (!vars.length) continue;
      // Upper bound in minutes (weekly cap is in hours)
      const upperBoundMin = Math.max(0, Math.round(Number(weeklyMaxByUserKey[userKey] || 0) * 60));
      constraints.push({ linear: { vars, coeffs }, domain: [-100000000, upperBoundMin] });
    }
  }
  return constraints;
}
